// StockPickerDialog — bring several products, in several lots, into an event
// in one go.
//
// The previous picker took one product and one lot per opening. An event that
// carries forty products in sixty lots meant sixty round-trips through a
// modal, and the owner's words for it were "masa harus manual cari satu-satu".
//
// Two panels. Left: every active product as a checklist, searchable. Right: for
// each checked product, a small table of lots — lot number, expired date, unit,
// form order, qty — with "+ Tambah lot". Submit walks the rows sequentially
// through set_event_allocation (the same upsert the importer uses), reports
// per-row failures, and invalidates the allocation query ONCE at the end rather
// than once per row.

import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Check, Plus, Trash2, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { Chip } from '@/components/ui/Chip';
import { Input } from '@/components/ui/Input';
import { setEventAllocation } from '@/lib/api/eventAllocations';
import { eventAllocationKeys } from '@/hooks/useEventAllocations';
import { formatIDR, formatNumber, isoDateToMs } from '@/lib/format';
import { cn } from '@/lib/format';
import type { EventStockAllocation, Product } from '@/types/domain';

export interface StockPickerDialogProps {
  eventId: string;
  products: Product[];
  allocations: EventStockAllocation[];
  /** Open with these products already ticked, one empty lot row each ("+ Lot"). */
  initialProductIds?: string[];
  onClose: () => void;
}

interface LotRow {
  key: string;
  lot: string;
  /** ISO yyyy-mm-dd or ''. */
  expiry: string;
  unit: string;
  orderForm: string;
  qty: string;
}

/** Mirrors the unique index: a null expiry has to match another null expiry. */
const batchKey = (productId: string, lot: string, expiry: string) =>
  `${productId}|${lot.trim()}|${expiry}`;

let rowSeq = 0;
const newRow = (unit: string): LotRow => ({
  key: `r${++rowSeq}`, lot: '', expiry: '', unit, orderForm: '', qty: '',
});

export function StockPickerDialog({ eventId, products, allocations, initialProductIds, onClose }: StockPickerDialogProps) {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [rowsByProduct, setRowsByProduct] = useState<Map<string, LotRow[]>>(() => {
    const m = new Map<string, LotRow[]>();
    for (const id of initialProductIds ?? []) {
      const p = products.find((x) => x.id === id);
      if (p) m.set(p.id, [newRow(p.unit ?? 'pcs')]);
    }
    return m;
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<{ ok: number; failed: { label: string; reason: string }[] } | null>(null);

  const byId = useMemo(() => new Map(products.map((p) => [p.id, p])), [products]);

  const existing = useMemo(() => {
    const m = new Map<string, EventStockAllocation>();
    for (const a of allocations) {
      const exp = a.expiryDate ? new Date(a.expiryDate).toISOString().slice(0, 10) : '';
      m.set(batchKey(a.productId, a.lotNumber ?? '', exp), a);
    }
    return m;
  }, [allocations]);

  const lotCount = useMemo(() => {
    const m = new Map<string, number>();
    for (const a of allocations) m.set(a.productId, (m.get(a.productId) ?? 0) + 1);
    return m;
  }, [allocations]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    const list = term
      ? products.filter((p) => p.name.toLowerCase().includes(term) || p.sku.toLowerCase().includes(term))
      : products;
    // Checked products float to the top so a long list does not hide what
    // has already been picked.
    return [...list].sort((a, b) => Number(rowsByProduct.has(b.id)) - Number(rowsByProduct.has(a.id)));
  }, [products, search, rowsByProduct]);

  const toggle = (p: Product, checked: boolean) => {
    setRowsByProduct((m) => {
      const next = new Map(m);
      if (checked) next.set(p.id, [newRow(p.unit ?? 'pcs')]);
      else next.delete(p.id);
      return next;
    });
  };

  const updateRow = (productId: string, key: string, patch: Partial<LotRow>) =>
    setRowsByProduct((m) => {
      const next = new Map(m);
      next.set(productId, (m.get(productId) ?? []).map((r) => (r.key === key ? { ...r, ...patch } : r)));
      return next;
    });

  const addRow = (p: Product) =>
    setRowsByProduct((m) => {
      const next = new Map(m);
      next.set(p.id, [...(m.get(p.id) ?? []), newRow(p.unit ?? 'pcs')]);
      return next;
    });

  const removeRow = (productId: string, key: string) =>
    setRowsByProduct((m) => {
      const next = new Map(m);
      const rows = (m.get(productId) ?? []).filter((r) => r.key !== key);
      if (rows.length === 0) next.delete(productId);
      else next.set(productId, rows);
      return next;
    });

  // Validation, per row: qty ≥ 1, no two rows naming the same batch, and a
  // note when the batch already exists (the upsert will replace its qty_in).
  const rowStatus = (productId: string, row: LotRow, siblings: LotRow[]) => {
    const qty = Number(row.qty);
    if (!row.qty.trim() || !Number.isInteger(qty) || qty < 1) return { error: 'Qty harus angka ≥ 1' };
    const k = batchKey(productId, row.lot, row.expiry);
    const dupes = siblings.filter((r) => r.key !== row.key && batchKey(productId, r.lot, r.expiry) === k);
    if (dupes.length > 0) return { error: 'Lot + tanggal sama dengan baris lain' };
    const ex = existing.get(k);
    if (ex) return { update: ex };
    return {};
  };

  const summary = useMemo(() => {
    let rows = 0, invalid = 0, updates = 0;
    for (const [pid, list] of rowsByProduct) {
      for (const r of list) {
        rows += 1;
        const st = rowStatus(pid, r, list);
        if ('error' in st) invalid += 1;
        else if ('update' in st) updates += 1;
      }
    }
    return { products: rowsByProduct.size, rows, invalid, updates };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowsByProduct, existing]);

  const submit = async () => {
    if (summary.rows === 0 || summary.invalid > 0) return;
    setBusy(true);
    const failed: { label: string; reason: string }[] = [];
    let ok = 0;
    // Sequential on purpose. set_event_allocation takes a row lock per batch,
    // and firing them in parallel would serialise in the database anyway while
    // making it impossible to say which row failed.
    for (const [pid, list] of rowsByProduct) {
      const p = byId.get(pid);
      for (const r of list) {
        const label = `${p?.name ?? pid}${r.lot ? ` · lot ${r.lot}` : ''}`;
        try {
          await setEventAllocation({
            eventId,
            productId: pid,
            qtyIn: Number(r.qty),
            lotNumber: r.lot.trim(),
            expiryDate: r.expiry ? isoDateToMs(r.expiry) : undefined,
            unit: r.unit.trim() || undefined,
            orderForm: r.orderForm.trim(),
            reason: 'Tambah stok (pilih produk)',
          });
          ok += 1;
        } catch (e) {
          failed.push({ label, reason: e instanceof Error ? e.message : 'Gagal' });
        }
      }
    }
    // One invalidation for the whole batch — per-row invalidation refetched the
    // table sixty times for a sixty-lot event.
    await qc.invalidateQueries({ queryKey: eventAllocationKeys.byEvent(eventId) });
    await qc.invalidateQueries({ queryKey: eventAllocationKeys.audit(eventId) });
    setBusy(false);
    setDone({ ok, failed });
  };

  const inputCls = 'input h-9 px-2 text-small';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-surface rounded-lg shadow-xl w-full max-w-5xl max-h-[92vh] flex flex-col">
        <div className="p-4 border-b border-line flex items-start justify-between gap-3">
          <div>
            <h3 className="text-subhead font-display font-semibold text-ink">Tambah stok ke event</h3>
            <p className="text-caption text-ink-subtle mt-1">
              Centang produk di kiri, lalu isi lot dan qty di kanan. Satu produk boleh beberapa lot.
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} aria-label="Tutup" disabled={busy}>
            <X size={16} />
          </Button>
        </div>

        {done ? (
          <div className="p-5 space-y-3 overflow-y-auto">
            <div className="p-4 rounded border border-success/30 bg-success/5">
              <p className="text-body font-medium text-ink inline-flex items-center gap-2">
                <Check size={16} className="text-success" />
                {formatNumber(done.ok)} lot tersimpan.
              </p>
            </div>
            {done.failed.length > 0 && (
              <div className="p-4 rounded border border-danger/30 bg-danger/5">
                <p className="text-small font-medium text-danger mb-2">{formatNumber(done.failed.length)} lot gagal:</p>
                <ul className="text-caption text-ink-subtle space-y-1 max-h-48 overflow-y-auto">
                  {done.failed.map((f, i) => <li key={i}><b>{f.label}</b> — {f.reason}</li>)}
                </ul>
              </div>
            )}
          </div>
        ) : (
          <div className="flex-1 min-h-0 grid md:grid-cols-[minmax(260px,1fr)_2fr]">
            {/* Left: checklist */}
            <div className="border-b md:border-b-0 md:border-r border-line flex flex-col min-h-0">
              <div className="p-3">
                <Input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari nama atau SKU…" />
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto px-3 pb-3 space-y-1.5">
                {filtered.length === 0 ? (
                  <p className="py-8 text-center text-caption text-ink-subtle">Tidak ada produk.</p>
                ) : (
                  filtered.map((p) => (
                    <Checkbox
                      key={p.id}
                      checked={rowsByProduct.has(p.id)}
                      onChange={(c) => toggle(p, c)}
                      label={p.name}
                      description={`${p.sku} · ${formatIDR(p.price)}${(lotCount.get(p.id) ?? 0) > 0 ? ` · sudah ada ${lotCount.get(p.id)} lot` : ''}`}
                      className="py-2"
                    />
                  ))
                )}
              </div>
            </div>

            {/* Right: lots per checked product */}
            <div className="flex flex-col min-h-0">
              <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-5">
                {rowsByProduct.size === 0 ? (
                  <div className="h-full min-h-[200px] flex items-center justify-center text-center text-small text-ink-subtle">
                    Belum ada produk dicentang.
                  </div>
                ) : (
                  Array.from(rowsByProduct.entries()).map(([pid, rows]) => {
                    const p = byId.get(pid);
                    if (!p) return null;
                    return (
                      <div key={pid} className="rounded-lg border border-line">
                        <div className="px-3 py-2 border-b border-line flex items-center justify-between gap-2 bg-canvas/50">
                          <div className="min-w-0">
                            <p className="text-small font-medium text-ink truncate">{p.name}</p>
                            <code className="text-caption font-mono text-ink-subtle">{p.sku}</code>
                          </div>
                          <Button variant="ghost" size="sm" leftIcon={<Plus size={14} />} onClick={() => addRow(p)}>
                            Tambah lot
                          </Button>
                        </div>
                        <div className="p-3 space-y-2">
                          <div className="hidden lg:grid grid-cols-[1.2fr_1.1fr_0.7fr_1fr_0.6fr_auto] gap-2 text-overline uppercase text-ink-subtle px-1">
                            <span>Lot number</span><span>Expired date</span><span>Satuan</span><span>Form order</span><span>Qty</span><span />
                          </div>
                          {rows.map((r) => {
                            const st = rowStatus(pid, r, rows);
                            return (
                              <div key={r.key} className="space-y-1">
                                <div className="grid grid-cols-2 lg:grid-cols-[1.2fr_1.1fr_0.7fr_1fr_0.6fr_auto] gap-2 items-center">
                                  <input className={inputCls} placeholder="Lot (boleh kosong)" value={r.lot} onChange={(e) => updateRow(pid, r.key, { lot: e.target.value })} aria-label="Lot number" />
                                  <input className={inputCls} type="date" value={r.expiry} onChange={(e) => updateRow(pid, r.key, { expiry: e.target.value })} aria-label="Expired date" />
                                  <input className={inputCls} placeholder="pcs" value={r.unit} onChange={(e) => updateRow(pid, r.key, { unit: e.target.value })} aria-label="Satuan" />
                                  <input className={inputCls} placeholder="FO-…" value={r.orderForm} onChange={(e) => updateRow(pid, r.key, { orderForm: e.target.value })} aria-label="Form order" />
                                  <input className={cn(inputCls, 'text-right tabular-nums', 'error' in st && 'border-danger')} type="number" min={1} inputMode="numeric" placeholder="0" value={r.qty} onChange={(e) => updateRow(pid, r.key, { qty: e.target.value })} aria-label="Qty" />
                                  <button type="button" onClick={() => removeRow(pid, r.key)} className="w-9 h-9 inline-flex items-center justify-center rounded text-ink-subtle hover:text-danger hover:bg-danger/5" aria-label="Hapus baris">
                                    <Trash2 size={14} />
                                  </button>
                                </div>
                                {'error' in st && <p className="text-caption text-danger px-1">{st.error}</p>}
                                {'update' in st && st.update && (
                                  <p className="text-caption text-ink-subtle px-1">
                                    Lot ini sudah ada (qty {formatNumber(st.update.qtyIn)}, terjual {formatNumber(st.update.qtySold)}) — qty akan <b>diganti</b> menjadi {r.qty || '…'}.
                                  </p>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        )}

        <div className="p-3 border-t border-line flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-caption text-ink-subtle">
            {!done && summary.rows > 0 && (
              <>
                <Chip variant="default">{summary.products} produk · {summary.rows} lot</Chip>
                {summary.updates > 0 && <Chip variant="info">{summary.updates} diperbarui</Chip>}
                {summary.invalid > 0 && (
                  <Chip variant="danger"><AlertTriangle size={12} className="inline mr-1" />{summary.invalid} belum valid</Chip>
                )}
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            {done ? (
              <Button variant="primary" onClick={onClose}>Selesai</Button>
            ) : (
              <>
                <Button variant="ghost" onClick={onClose} disabled={busy}>Batal</Button>
                <Button variant="primary" onClick={submit} loading={busy} disabled={summary.rows === 0 || summary.invalid > 0}>
                  Simpan {summary.rows > 0 ? `${summary.rows} lot` : ''}
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
